import { formatISO } from 'date-fns';

import { Url } from '@catawiki/runtime-toolkit';
import { RFC4646Locale, InternalLocale } from '@catawiki/locale';

import { alphabeticalSort } from '@/shared/helpers/sort';
import {
  CategoryId,
  CouriersAvailabilityQuery,
  ManualShippingConfig,
  ShippingProfile,
  ShippingSuggestedRatesQuery,
} from '@/shared/models/shipping';
import { IntervalValue, StatType } from '@/shared/types/dashboard';
import { envEncoder, filterNaNToNull } from '@/shared/utils/queryString';

import { LotEmbedFields } from '../types/lot';

export const getServersideBaseApiUrl = () =>
  `http://${process.env.CW_API_ENDPOINT}`;

export const sellerAPI = '/seller/api/v1';
export const sellerAPIV2 = '/seller/api/v2';
export const paymentsAPIV2 = '/payments/api/v2';
const ordersAPI = '/orders/api/v3';

export const actionableLots = `${sellerAPI}/lots/actionable_lots`;
export const actionableLotsHbo = `${actionableLots}/hbo`;
export const offeredLotsAPIURL = `${sellerAPI}/lots/offered-lots`;

const baseFeatureFeedbacks = `${sellerAPI}/feature_feedbacks`;
export const getFeatureFeedbackUrl = (name: string) =>
  `${baseFeatureFeedbacks}/${name}`;
export const createFeatureFeedbackUrl = baseFeatureFeedbacks;
export const updateFeatureFeedbackUrl = (id: number) =>
  `${baseFeatureFeedbacks}/${id}`;

export const baseLotApiPath = (lotId: number) => `${sellerAPI}/lots/${lotId}`;

export const highestBidOffer = (lotId: number) =>
  `${baseLotApiPath(lotId)}/highest_bid_offer`;

export const userStatsCharts = (
  from: Date,
  to: Date,
  interval: IntervalValue
) =>
  `${sellerAPI}/users/statistics/graph${Url.createSearch(
    {
      interval,
      from: formatISO(from, { representation: 'date' }),
      to: formatISO(to, { representation: 'date' }),
      stats: Object.values(StatType),
    },
    { addQueryPrefix: true, arrayFormat: 'comma', sort: alphabeticalSort }
  )}`;

export const userStatsOverview = (from: Date, to: Date) =>
  `${sellerAPI}/users/statistics/overview${Url.createSearch(
    {
      from: formatISO(from, { representation: 'date' }),
      to: formatISO(to, { representation: 'date' }),
    },
    { addQueryPrefix: true, sort: alphabeticalSort }
  )}`;

export const userStatsScore = () => `${sellerAPI}/users/me/statistics`;

export const sellerProfileAPI = `${sellerAPI}/users/me/profile`;
export const sellerPreferencesEndpoint = `${sellerAPI}/seller_preferences`;
export const payoutProfileAPI = `${paymentsAPIV2}/payouts/profiles`;
export const userTodosEndpoint = `${sellerAPI}/users/me/todos`;

export const userAuthenticatedAPI = `${getServersideBaseApiUrl()}${sellerAPI}/users/me/authenticated`;
export const userSSRSellerProfileAPI = `${getServersideBaseApiUrl()}${sellerProfileAPI}`;
export const userSSRPayoutProfileAPI = `${getServersideBaseApiUrl()}${payoutProfileAPI}`;

export const dac7StatusApiPath = `${sellerAPI}/dac7/status`;

export const sellerOrdersAPI = `${ordersAPI}/users/seller/orders`;

export const lotDetailsEndpoint = (lotId: number | string) =>
  `${sellerAPI}/lots/${lotId}`;

export const sellerScoresEndpoint = `${sellerAPI}/seller_scores`;

export const lotDetailsWithQueryEndpoint = (
  lotId: number,
  locale: InternalLocale,
  fields: LotEmbedFields[] = []
) =>
  `${lotDetailsEndpoint(lotId)}${Url.createSearch(
    {
      locale,
      embed: fields,
    },
    { addQueryPrefix: true, arrayFormat: 'comma', sort: alphabeticalSort }
  )}`;

export const lotConfigurationEndpoint = (lotId: number) =>
  `${lotDetailsEndpoint(lotId)}/configuration`;

export const lotPropertiesEndpoint = (lotId: number) =>
  `${sellerAPIV2}/lots/${lotId}/properties`;

export const lotSpecifications = (lotId: number) =>
  `${sellerAPIV2}/lots/${lotId}/specifications`;

export const duplicateLotEndpoint = (lotId: number) =>
  `${lotDetailsEndpoint(lotId)}/duplicate`;

export const lotCopyCheckEndpoint = (lotId: number) =>
  `${lotDetailsEndpoint(lotId)}/copy_check`;

export const lotImagesEndpoint = (lotId: number) =>
  `${lotDetailsEndpoint(lotId)}/images`;

export const imageCaptureSyncEmailEndpoint = (lotId: number, email: string) =>
  `${lotDetailsEndpoint(lotId)}/image_capture/email?email=${email}`;

export const imageUploadEndpoint = `${sellerAPI}/lots/images/upload`;

const imagesAPI = '/images/api/v1';
export const rotateImageEndpoint = `${imagesAPI}/images/`;

export const specificationsEndpoint = `${sellerAPIV2}/specifications`;

export const objectTypesEndpoint = `${sellerAPI}/object_types`;

export const objectTypeEndpoint = (id: number) =>
  `${objectTypesEndpoint}/${id}`;

export const lotheaderTemplateEndpoint = (id: number) =>
  `${objectTypeEndpoint(id)}/templates`;

export const categoriesEndpoint = `${sellerAPI}/categories`;

export const categoryEndpoint = (categoryId: string | number) =>
  `${sellerAPI}/categories/${categoryId}`;

export const themedAuctionsEndpoint = (categoryId: string | number) =>
  `${categoryEndpoint(categoryId)}/themes`;

export const recentCategoriesEndpoint = `${categoriesEndpoint}/recent`;

export const lotLanguageDetect = `${sellerAPI}/lots/languages/detect`;
export const lotMessages = (lotId: number) =>
  `${sellerAPI}/lots/${lotId}/messages`;

export const lotVideos = (lotId: number) =>
  `${lotDetailsEndpoint(lotId)}/videos`;

export const lotShippingConfiguration = (lotId: number) =>
  `${sellerAPI}/shipping/lots/${lotId}/configuration`;

const shippingAPIV2 = '/shipping/api/v2';

export const shippingConfigurations = (lotIds: number[]) =>
  `${sellerAPI}/shipping/configurations${Url.createSearch(
    {
      lot_ids: lotIds,
    },
    { addQueryPrefix: true, arrayFormat: 'comma', sort: alphabeticalSort }
  )}`;

const shippingFeatureFlags = `${shippingAPIV2}/seller/feature-flags`;
const shippingSuggestedRates = (query: ShippingSuggestedRatesQuery) =>
  `${shippingAPIV2}/suggested-rates${Url.createSearch(query, {
    addQueryPrefix: true,
    filter: filterNaNToNull,
    sort: alphabeticalSort,
  })}`;
export const shippingZones = `${sellerAPI}/shipping/zones`;
const shippingCostThresholdsEndpoint = (
  lotId: ManualShippingConfig['lot_id'],
  categoryId?: CategoryId,
  packageDimensions?: ManualShippingConfig['package_dimensions'],
  packageId?: ManualShippingConfig['package_preset_id']
) =>
  `${shippingAPIV2}/lot/cost-caps${Url.createSearch(
    {
      category_id: categoryId,
      custom_package_dimensions: packageDimensions,
      package_preset_id: packageId,
      lot_id: lotId,
    },
    {
      addQueryPrefix: true,
      encoder: envEncoder,
      sort: alphabeticalSort,
    }
  )}`;
const shippingProfiles = `${shippingAPIV2}/seller/shipping-profiles`;
const shippingProfile = (id: ShippingProfile['id']) =>
  `${shippingAPIV2}/seller/shipping-profiles/${id}`;
const shippingMethods = (lotId?: number) =>
  `${shippingAPIV2}/seller/shipping-method${
    lotId ? Url.createSearch({ lot_id: lotId }, { addQueryPrefix: true }) : ''
  }`;
const preferredShippingCompanies = `${shippingAPIV2}/seller/smart-preferred-couriers`;
const setPreferredShippingMethod = `${shippingAPIV2}/seller/shipping_method_configuration`;
const packagingAndHandlingFeeThresholds = `${shippingAPIV2}/handling_fee_configuration`;
const shippingPackagePresets = `${shippingAPIV2}/package_presets`;
const couriersAvailability = (query: CouriersAvailabilityQuery) =>
  `${shippingAPIV2}/smart/couriers_availability${Url.createSearch(query, {
    addQueryPrefix: true,
    arrayFormat: 'brackets',
    encoder: envEncoder,
    sort: alphabeticalSort,
  })}`;
const shippingCompareRatesEndpoint = (lotId: number) =>
  `${shippingAPIV2}/lot/${lotId}/compare-rates`;

const insuranceAPI = '/insurance/api/v1';

export type InsuranceAvailabilityQuery = {
  categoryId: number;
  countryCode: string;
  locale: RFC4646Locale;
  estimatedPrice: number;
  selectedCouriers: string[];
};

export const insuranceCoverageAvailability = ({
  categoryId,
  countryCode,
  locale,
  estimatedPrice,
  selectedCouriers,
}: InsuranceAvailabilityQuery) =>
  `${insuranceAPI}/coverage${Url.createSearch(
    {
      origin_country: countryCode,
      l2_category_id: categoryId,
      locale,
      estimated_price: estimatedPrice ?? 0,
      selected_couriers: selectedCouriers ?? [],
    },
    { addQueryPrefix: true, arrayFormat: 'comma' }
  )}`;

export const shipping = {
  configurations: shippingConfigurations,
  featureFlags: shippingFeatureFlags,
  suggestedRates: shippingSuggestedRates,
  zones: shippingZones,
  costThresholds: shippingCostThresholdsEndpoint,
  profiles: shippingProfiles,
  profile: shippingProfile,
  preferredCompanies: preferredShippingCompanies,
  methods: shippingMethods,
  setPreferredMethod: setPreferredShippingMethod,
  packagingAndHandlingFeeThresholds,
  config: lotShippingConfiguration,
  packagePresets: shippingPackagePresets,
  couriersAvailability,
  insuranceCoverageAvailability,
  compareRates: shippingCompareRatesEndpoint,
};

export const liveLots = `${sellerAPI}/lots/live`;

export const archiveLotsAPIURL = `${sellerAPI}/lots/archive`;

export const accountsAPI = '/accounts/api/v1';

export const accountsUserUrl = (userId: string) =>
  `${accountsAPI}/users/${userId}`;

export const category = (categoryId: string | number) =>
  `${sellerAPI}/categories/${categoryId}`;
export const categoryRPThreshold = (categoryId: number) =>
  `${category(categoryId)}/reoffer_rp_thresholds`;

export const duplicateLot = (lotId: number) =>
  `${lotDetailsEndpoint(lotId)}/duplicate`;

export const lotWithEmbedFields = (
  lotId: number,
  locale: InternalLocale,
  fields: LotEmbedFields[] = []
) =>
  `${lotDetailsEndpoint(lotId)}${Url.createSearch(
    {
      locale,
      embed: fields,
    },
    { addQueryPrefix: true, arrayFormat: 'comma' }
  )}`;

export const lotProperties = (lotId: number) =>
  `${lotDetailsEndpoint(lotId)}/properties`;

export const categoryTemplates = (categoryId: number) =>
  `${sellerAPI}/categories/${categoryId}/templates`;

export const themedAuctions = (categoryId: string | number) =>
  `${category(categoryId)}/themes`;

export const lotPropertiesV2 = (lotId: number) =>
  `${sellerAPIV2}/lots/${lotId}/properties`;

export const lotCompletions = (lotId: number) =>
  `${sellerAPI}/lots/${lotId}/completions`;

export const lotCompletionsStatus = (lotId: number, completionId: number) =>
  `${sellerAPI}/lots/${lotId}/completions/${completionId}/status`;
